/**
 * Shared helpers for rendering a grounding source's origin.
 *
 * Both surfaces that show provenance — the inline citation badge and the
 * sources popover — need the same four answers: is this a Bower link, what's
 * its favicon, what's its domain, and what metadata can we lazily fetch for
 * it. Keeping them here means the two can't drift into disagreeing about
 * where a citation came from.
 */

import { useEffect, useState } from 'react';

import { fetchUrlMetadata, type UrlMetadata } from '../../../services/urlMetadata.service';

/**
 * Internal app link (relative path like /notes/abc) rather than the web.
 *
 * `//evil.example/x` is protocol-relative — it leaves the app — so it must NOT
 * pass. The sources list labels an internal row with an affirmative "Bower"
 * and opens it same-tab without `rel`, so a false positive there is a
 * first-party attribution on someone else's page.
 */
export function isInternalUrl(url: string): boolean {
  // Resolve it and compare origins. Do NOT pattern-match the string.
  //
  // Two earlier versions of this function tried: `startsWith('/') &&
  // !startsWith('//')` missed `/\evil.example/x` (browsers normalise the
  // backslash to a slash), and the regex that fixed THAT still missed
  // `/\t/evil.example/x` — WHATWG URL parsing strips tab/LF/CR from the input
  // BEFORE parsing, so the second character isn't a slash at match time but is
  // by the time it resolves. Both resolve off-site.
  //
  // That matters because the sources list labels an internal row with an
  // affirmative "Bower" and opens it same-tab with no `rel`, so a false
  // positive is first-party attribution on someone else's page — and the URL
  // is attacker-influenceable (Bird restates connector and web-search content).
  // Resolution is the same operation the browser will perform, so it can't
  // disagree with it the way a pattern can.
  // The leading-slash requirement is separate from the origin check and both
  // are load-bearing: `''` and `notes/abc` resolve to the sentinel's own
  // origin, so origin alone would call them internal.
  if (!url.startsWith('/')) return false;
  try {
    return new URL(url, INTERNAL_ORIGIN_SENTINEL).origin === INTERNAL_ORIGIN_SENTINEL;
  } catch {
    return false;
  }
}

/**
 * Unresolvable base for origin comparison. `.invalid` is reserved by RFC 2606
 * and never resolves, so even if a source URL names it verbatim the "internal"
 * verdict points nowhere.
 */
const INTERNAL_ORIGIN_SENTINEL = 'https://bower-internal.invalid';

/** Favicon URL — prefer the fetched one, fall back to Google's service. */
export function getFaviconUrl(url: string, fetchedFavicon?: string | null): string {
  if (fetchedFavicon) return fetchedFavicon;
  if (isInternalUrl(url)) return '';
  try {
    const domain = new URL(url).hostname;
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
  } catch {
    return '';
  }
}

/** Domain for display, returning the fallback for internal or malformed URLs. */
export function getSafeDomain(url: string, fallback: string): string {
  if (isInternalUrl(url)) return fallback;
  try {
    return new URL(url).hostname;
  } catch {
    return fallback;
  }
}

/**
 * Lazily loads title/favicon/domain for an external URL. Skips internal app
 * URLs entirely — we already know what those are — and only fires once
 * `shouldFetch` is true, so a collapsed citation costs no requests.
 */
export function useUrlMetadata(url: string, shouldFetch: boolean) {
  const [metadata, setMetadata] = useState<UrlMetadata | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!shouldFetch || metadata || isInternalUrl(url)) return;

    setIsLoading(true);
    fetchUrlMetadata(url)
      .then(setMetadata)
      .finally(() => setIsLoading(false));
  }, [url, shouldFetch, metadata]);

  return { metadata, isLoading };
}
